import React, { useRef, useEffect, useState, ReactNode } from 'react';

interface ScrollRevealProps {
  children: ReactNode;
  className?: string;
  threshold?: number; // 0 to 1 (percentage of element visible)
  animation?: 'fade-in' | 'fade-in-up' | 'fade-in-down' | 'fade-in-left' | 'fade-in-right' | 'zoom-in';
  delay?: number; // milliseconds
  rootMargin?: string; // CSS margin value
  triggerOnce?: boolean;
}

/**
 * Tesla-inspired ScrollReveal component for animating elements when they enter the viewport
 */
const ScrollReveal: React.FC<ScrollRevealProps> = ({
  children,
  className = '',
  threshold = 0.1,
  animation = 'fade-in-up',
  delay = 0,
  rootMargin = '0px',
  triggerOnce = true,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      {
        root: null, // viewport
        rootMargin,
        threshold,
      }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [rootMargin, threshold, triggerOnce]);

  // Prepare animation class and delay style
  const animationClass = isVisible ? animation : '';
  const delayStyle = delay ? { animationDelay: `${delay}ms` } : {};

  return (
    <div
      ref={elementRef}
      className={`${className} ${animationClass}`}
      style={{
        ...delayStyle,
        opacity: isVisible ? 1 : 0, // Base opacity
        transition: 'opacity 0.3s ease-out',
      }}
    >
      {children}
    </div>
  );
};

export default ScrollReveal;